import { View, StyleSheet, Text, Pressable, Image } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { Colors } from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useEffect, useState } from "react";
import Icon from "./Icon";
import { useAuth } from "@/models/useAuth";
import { App } from "@capacitor/app";
import { SubscriptionPlan } from "@/models/types";
import { useUserSlice } from "@/models/useUserSlice";
import { gotoPrivacy, gotoTerms } from "@/models/utils";

type PlanOption = {
  title: string;
  price: string;
  discount?: string;
  plan: SubscriptionPlan;
};

const Feature = ({ textKey }: { textKey: string }) => {
  const { t } = useTranslation();

  return (
    <View style={styles.featureContainer}>
      <Icon icon="tickCircle" style={styles.tickIcon} />
      <Text style={styles.featureText}>{t(textKey)}</Text>
    </View>
  );
};

const PlanSelector = ({
  plan,
  selectedPlan,
  onSelect,
}: {
  plan: PlanOption;
  selectedPlan: SubscriptionPlan;
  onSelect: (plan: SubscriptionPlan) => void;
}) => {
  const isSelected = selectedPlan === plan.plan;
  const { t } = useTranslation();

  return (
    <Pressable
      key={plan.title}
      style={[
        styles.planButton,
        { borderColor: isSelected ? Colors.accent : Colors.text + "30" },
      ]}
      onPress={() => onSelect(plan.plan)}
    >
      <View style={isSelected ? styles.radioOuterSelected : styles.radioOuter}>
        <View
          style={isSelected ? styles.radioInnerSelected : styles.radioInner}
        />
      </View>
      <View style={styles.planContent}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text style={styles.planTitle}>{plan.title}</Text>
          {plan.discount && (
            <Text style={styles.discountText}>{plan.discount}</Text>
          )}
        </View>
        <Text style={styles.planPrice}>
          {plan.price}
          <Text
            style={{
              marginLeft: ms(1),
              fontSize: ms(12),
              color: Colors.text + "60",
            }}
          >
            {plan.plan === "YEARLY_MID_TIER"
              ? t("alertWidgetPaywall.perYear")
              : t("alertWidgetPaywall.perMonth")}
          </Text>
        </Text>
      </View>
    </Pressable>
  );
};

const AlertWidgetPaywall = () => {
  const visible = useUISlice((state) => state.paywallVisible);
  const setVisible = useUISlice((state) => state.setPaywallVisible);
  const [selectedPlan, setSelectedPlan] =
    useState<SubscriptionPlan>("YEARLY_MID_TIER");
  const { t } = useTranslation();
  const width = useUISlice((state) => state.device.width);

  useEffect(() => {
    const listenBackButton = () => {
      App.addListener("backButton", () => {
        setVisible(undefined);
      });
    };

    listenBackButton();

    return () => {
      App.removeAllListeners();
    };
  }, [visible]);

  let plans: PlanOption[] = [
    {
      title: t("alertWidgetPaywall.monthly"),
      price: "US$19.99",
      plan: "MONTHLY_MID_TIER",
    },
    {
      title: t("alertWidgetPaywall.annual"),
      discount: t("alertWidgetPaywall.discountText", {
        discount: "US$140",
      }),
      price: "US$99.99",
      plan: "YEARLY_MID_TIER",
    },
  ];

  const onPress = async () => {
    const onPlanSelected = async () => {
      useUserSlice.getState().subscription().stripeCheckout(selectedPlan);
    };

    if (!useAuth.isAuthenticated()) {
      useAuth.signIn({
        callback: () => {
          onPlanSelected();
        },
      });

      return;
    }

    onPlanSelected();
  };

  const _gotoTerms = () => {
    App.removeAllListeners();
    gotoTerms();
  };

  const _gotoPrivacy = () => {
    App.removeAllListeners();
    gotoPrivacy();
  };

  if (!visible) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetPaywall.title")}
      subtitle={t("alertWidgetPaywall.subtitle")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.features}>
        <Feature textKey="alertWidgetPaywall.feature1" />
        <Feature textKey="alertWidgetPaywall.feature2" />
        <Feature textKey="alertWidgetPaywall.feature3" />
      </View>

      <View style={styles.plansContainer}>
        {plans.map((plan) => (
          <PlanSelector
            key={plan.plan}
            plan={plan}
            selectedPlan={selectedPlan}
            onSelect={setSelectedPlan}
          />
        ))}
      </View>

      <PrimaryButton text={t("alertWidgetPaywall.button")} onPress={onPress} />

      <View style={styles.joinContainer}>
        <View style={styles.avatarsContainer}>
          <Image
            source={{ uri: "/icons/paywall_avatar1.jpg" }}
            style={[styles.avatar, { marginLeft: 0 }]}
          />
          <Image
            source={{ uri: "/icons/paywall_avatar2.jpg" }}
            style={styles.avatar}
          />
          <Image
            source={{ uri: "/icons/paywall_avatar3.jpg" }}
            style={styles.avatar}
          />
        </View>

        <Text style={styles.joinText}>{t("alertWidgetPaywall.joinText")}</Text>
      </View>

      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  negativeMargin: {
    marginBottom: -ms(12),
  },
  features: {
    marginBottom: ms(16),
  },
  featureContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(8),
    marginBottom: ms(6),
  },
  discountText: {
    fontSize: ms(12),
    fontWeight: "500",
    lineHeight: ms(24),
    color: Colors.text,
    paddingHorizontal: ms(12),
    paddingVertical: ms(1),
    backgroundColor: "#00ff00" + "20",
    borderRadius: ms(8),
    marginLeft: ms(12),
  },
  avatar: {
    borderWidth: ms(2),
    borderColor: Colors.text + "30",
    borderRadius: ms(24),
    width: ms(18),
    height: ms(18),
    marginLeft: -ms(2),
  },
  featureText: {
    fontSize: ms(14),
    fontWeight: "400",
    lineHeight: ms(24),
    color: Colors.text,
  },
  tickIcon: {
    tintColor: "#15803d",
    width: ms(16),
    height: ms(16),
  },
  planButton: {
    borderWidth: ms(2),
    height: ms(60),
    paddingHorizontal: ms(16),
    borderRadius: ms(12),
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: ms(12),
  },
  radioOuter: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.text + "30",
    alignItems: "center",
    justifyContent: "center",
  },
  radioInner: {
    width: ms(14),
    height: ms(14),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  radioOuterSelected: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.accent,
    alignItems: "center",
    justifyContent: "center",
  },
  radioInnerSelected: {
    width: ms(8),
    height: ms(8),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  planContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: ms(12),
  },
  planTitle: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
  },
  planPrice: {
    fontSize: ms(14),
    color: Colors.text,
  },
  plansContainer: {
    marginVertical: ms(16),
  },
  joinContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: ms(12),
    gap: ms(4),
  },
  avatarsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: -ms(4),
  },
  joinText: {
    marginLeft: ms(8),
    fontSize: ms(12),
    color: Colors.subtitle,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: ms(32),
    marginTop: ms(12),
  },
  footerLink: {
    fontSize: ms(14),
    color: Colors.subtitle,
  },
});

export default AlertWidgetPaywall;
